import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';
import { HeaderPage } from 'components/HeaderPage/HeaderPage';
import Button from 'components/Button';
import { DASHBOARD_ROUTES } from '../../Routes';
import {
  finishGmailConnection,
  getGmailConnectionStatus,
  GmailConnectionStatus,
  startGmailConnection,
} from 'data/api/gmailConnection.api';

// The poller runs every minute, so ten minutes without a successful check is
// a broken connection (usually support@'s password changed), not a slow one.
const STALE_AFTER_MINUTES = 10;
const REFRESH_MS = 30 * 1000;

type Health = 'unavailable' | 'disconnected' | 'paused' | 'healthy' | 'stale';

// A check from before the latest connect says nothing about this connection:
// right after a reconnect it is the old, failing one.
const lastCheckOf = (status: GmailConnectionStatus): string | null => {
  if (!status.lastPollDate) return null;
  if (
    status.connectedDate &&
    moment(status.lastPollDate).isBefore(moment(status.connectedDate))
  ) {
    return null;
  }
  return status.lastPollDate;
};

const healthOf = (status: GmailConnectionStatus): Health => {
  if (!status.configured) return 'unavailable';
  if (!status.connected) return 'disconnected';
  if (!status.pollingEnabled) return 'paused';
  const since = lastCheckOf(status) ?? status.connectedDate;
  const minutes = since ? moment().diff(moment(since), 'minutes') : 0;
  return minutes > STALE_AFTER_MINUTES ? 'stale' : 'healthy';
};

const HEALTH_LABEL: Record<Health, { label: string; color: string }> = {
  unavailable: { label: 'Not set up yet', color: '#6B7280' },
  disconnected: { label: 'Not connected', color: '#6B7280' },
  paused: { label: 'Connected, not being checked', color: '#6B7280' },
  healthy: { label: 'Connected', color: '#2E7D32' },
  stale: { label: 'Needs attention', color: '#B45309' },
};

const bodyText = { color: '#4A5568' };

type Notice = { severity: 'error' | 'info'; text: string };

export const SupportInboxPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<GmailConnectionStatus>();
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [busy, setBusy] = React.useState<'starting' | 'finishing' | null>(null);
  const [notice, setNotice] = React.useState<Notice>();
  const handledReturn = React.useRef(false);

  const load = React.useCallback(async () => {
    setLoadFailed(false);
    try {
      setStatus(await getGmailConnectionStatus());
    } catch (error) {
      setLoadFailed(true);
    }
  }, []);

  // Google sends the browser back here with ?code&state (or ?error). The code
  // is single-use: the query is cleared before posting, so a reload or the
  // back button cannot replay a spent one.
  React.useEffect(() => {
    if (handledReturn.current) return;
    handledReturn.current = true;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const googleError = params.get('error');

    if (!code && !googleError) {
      void load();
      return;
    }

    navigate(DASHBOARD_ROUTES.ROUTE_SUPPORT_INBOX, { replace: true });

    if (googleError || !state || !code) {
      setNotice(
        googleError === 'access_denied'
          ? {
              severity: 'info',
              text: 'Google sign-in was cancelled, so nothing was connected.',
            }
          : {
              severity: 'error',
              text: 'Google sign-in did not finish. Please try again.',
            },
      );
      void load();
      return;
    }

    setBusy('finishing');
    finishGmailConnection(code, state)
      .then((connected) => {
        setStatus(connected);
        toast.success('Support inbox connected');
      })
      .catch((error: Error) => {
        setNotice({
          severity: 'error',
          text:
            error.message ||
            'The support inbox could not be connected. Please try again.',
        });
        void load();
      })
      .finally(() => setBusy(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps "Last checked" honest while the page is open: a first check landing,
  // or a connection going stale, shows up without a reload. A failed refresh
  // keeps the last good status rather than flashing an error, but the tick
  // still re-renders, so freshness is judged against the clock and a status
  // nobody can refresh goes stale on its own instead of staying green.
  const loaded = !!status;
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!loaded || busy !== null) return;
    const timer = window.setInterval(() => {
      setTick((tick) => tick + 1);
      getGmailConnectionStatus()
        .then(setStatus)
        .catch(() => undefined);
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [loaded, busy]);

  // Back from Google's screen can restore this page from the browser's cache
  // mid-"Opening Google…", with the button still disabled.
  React.useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setBusy(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const connect = async () => {
    setNotice(undefined);
    setBusy('starting');
    try {
      const { url } = await startGmailConnection();
      window.location.assign(url);
    } catch (error) {
      setNotice({
        severity: 'error',
        text:
          (error as Error).message ||
          'Could not start Google sign-in. Please try again.',
      });
      setBusy(null);
    }
  };

  const health = status ? healthOf(status) : undefined;
  const mailbox = status?.mailbox || 'support@betterleave.com';

  const renderDetails = (current: GmailConnectionStatus, state: Health) => {
    const lastCheck = lastCheckOf(current);

    switch (state) {
      case 'unavailable':
        return (
          <Typography variant="body2" sx={bodyText}>
            Google sign-in isn&apos;t set up for this site yet. Ask the
            engineering team to finish setup; there&apos;s nothing to do here
            until then.
          </Typography>
        );
      case 'disconnected':
        return (
          <Typography variant="body2" sx={bodyText}>
            Replies to triage emails aren&apos;t being filed onto their triages
            yet.
          </Typography>
        );
      case 'paused':
        return (
          <Typography variant="body2" sx={bodyText}>
            Betterleave isn&apos;t checking this inbox right now, so replies
            won&apos;t show up on triages. Reconnecting won&apos;t fix this; let
            the engineering team know.
          </Typography>
        );
      case 'stale':
        return (
          <Typography variant="body2" sx={bodyText}>
            {lastCheck
              ? `Betterleave hasn't been able to check the inbox for ${moment(
                  lastCheck,
                ).toNow(true)}.`
              : "Betterleave hasn't been able to check the inbox since it was connected."}{' '}
            This usually means the password for {mailbox} was changed. Reconnect
            below; replies that arrive in the meantime will be filed once
            it&apos;s reconnected.
          </Typography>
        );
      case 'healthy':
        return (
          <Typography variant="body2" sx={bodyText}>
            {lastCheck
              ? `Last checked ${moment(lastCheck).fromNow()}`
              : 'First check happens within a minute.'}
          </Typography>
        );
    }
  };

  const renderStatus = () => {
    if (busy === 'finishing' || (!status && !loadFailed)) {
      return (
        <Stack direction="row" alignItems="center" gap={1.5}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={bodyText}>
            {busy === 'finishing'
              ? 'Finishing the connection…'
              : 'Checking the connection…'}
          </Typography>
        </Stack>
      );
    }

    if (loadFailed || !status || !health) {
      return (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => void load()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t load the connection status.
        </Alert>
      );
    }

    const { label, color } = HEALTH_LABEL[health];

    return (
      <Stack gap={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: '#213B54' }}
          >
            {mailbox}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: color,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color }}>
              {label}
            </Typography>
          </Stack>
        </Stack>

        {renderDetails(status, health)}

        {status.connected && health !== 'unavailable' && (
          <Typography variant="body2" sx={bodyText}>
            Connected
            {status.connectedByName
              ? ` by ${status.connectedByName}`
              : ''} on{' '}
            {moment(status.connectedDate).format('MMM D, YYYY [at] h:mm A')}
          </Typography>
        )}
      </Stack>
    );
  };

  const needsAction = health === 'disconnected' || health === 'stale';
  const showConnect = !!status?.configured && busy !== 'finishing';

  return (
    <>
      <HeaderPage title={{ title: 'Support inbox connection' }} tabs={[]} />

      <div className="nv-container">
        <Stack gap={3} sx={{ maxWidth: 640, mt: 3 }}>
          <Typography variant="body2" sx={bodyText}>
            When someone replies to a triage email, the reply lands in the
            support inbox. Betterleave reads that inbox to file each reply back
            onto its triage.
          </Typography>

          {notice && (
            <Alert
              severity={notice.severity}
              onClose={() => setNotice(undefined)}
            >
              {notice.text}
            </Alert>
          )}

          <Box sx={{ border: '1px solid #EFEEF0', borderRadius: '8px', p: 3 }}>
            {renderStatus()}
          </Box>

          {showConnect && (
            <Stack gap={2} alignItems="flex-start">
              {needsAction && (
                <Typography variant="body2" sx={bodyText}>
                  You&apos;ll need the password for {mailbox}. Google will ask
                  you to let Betterleave view that inbox&apos;s email. That is
                  read-only access: Betterleave can&apos;t send, change, or
                  delete anything, and no other inbox is affected.
                </Typography>
              )}
              <Button
                variant={needsAction ? 'contained' : 'outlined'}
                disabled={busy !== null}
                onClick={() => void connect()}
              >
                {busy === 'starting'
                  ? 'Opening Google…'
                  : status?.connected
                  ? 'Reconnect'
                  : 'Connect support inbox'}
              </Button>
            </Stack>
          )}
        </Stack>
      </div>
    </>
  );
};

export default SupportInboxPage;
